import { PropsWithChildren } from 'react';

import { LoadingState, StateMessage } from '@/components/StateMessage';
import { useSession } from '@/features/session/SessionProvider';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import WebAssetOutlinedIcon from '@mui/icons-material/WebAssetOutlined';
import { Button, Typography } from '@mui/joy';

/** The chat's pages behind the session: the children only when signed in, the session's state otherwise. */
export const SessionGate = ({ children }: PropsWithChildren) => {
  const { mode, state, signIn, reopenWindow, retry } = useSession();

  // The panel signs in through a window opened from this click (ADR-0004 §2.4), the full page by a redirect.
  const signInButton = <Button onClick={signIn}>Увійти</Button>;

  switch (state.status) {
    case 'signedIn':
      return <>{children}</>;
    case 'checking':
    case 'signingIn':
      return <LoadingState />;
    case 'loginRequired':
      return (
        <StateMessage
          icon={<LoginIcon />}
          title='Увійдіть, щоб користуватися чатом'
          text={
            mode === 'embed'
              ? 'Вхід відкриється в окремому малому вікні. Після входу чат запрацює тут.'
              : 'Увійдіть обліковим записом Gasbi, щоб продовжити.'
          }
        >
          {state.popupBlocked && (
            <Typography level='body-sm' color='warning'>
              Дозвольте спливні вікна для цього сайту
            </Typography>
          )}
          {signInButton}
        </StateMessage>
      );
    case 'waitingForWindow':
      return (
        <StateMessage
          icon={<WebAssetOutlinedIcon />}
          title='Очікуємо вхід у вікні…'
          text='Завершіть вхід у вікні, що відкрилося. Не бачите його? Браузер міг заблокувати вікно.'
        >
          <Button variant='outlined' color='neutral' onClick={reopenWindow}>
            Відкрити вікно ще раз
          </Button>
        </StateMessage>
      );
    case 'userMismatch':
      return (
        <StateMessage
          icon={<PeopleOutlineIcon />}
          title='Чат відкрито під іншим користувачем'
          text={`Чат працює під обліковим записом ${state.user.name}, а в додатку — інший користувач. Увійдіть під своїм.`}
        >
          {signInButton}
        </StateMessage>
      );
    case 'noAccess':
      return (
        <StateMessage
          icon={<LockOutlinedIcon />}
          color='danger'
          title='Немає доступу'
          text='Ваш обліковий запис не має доступу до Gasbi AI. Зверніться до адміністратора.'
        />
      );
    case 'error':
      return (
        <StateMessage
          icon={<ErrorOutlineIcon />}
          color='danger'
          title='Не вдалося перевірити вхід'
          text='Сервіс Gasbi AI не відповів. Спробуйте ще раз за мить.'
        >
          <Button onClick={retry}>Спробувати ще раз</Button>
        </StateMessage>
      );
  }
};
